import { OmneoList, OmneoListDefinitionType, OmneoListItem, typeInStoreItemSupplementalParsed, typeInStoreItemSupplementalRaw, typeProduct } from '../types'
import { OmneoListItemInStore } from '../types/omneo-wishlist-item'
import { type_GM_STATE } from '../../helpers'
import { fetchData } from '../../helpers/fetch'
import { getManyProductsByManyHandles, getManyProductsByManySkus } from '../../helpers/storefront-graphql-queries'
import { executeGraphQlQuery, getIdFromGqlAsNumber } from '../../helpers/storefront-graphql-helper'

declare var GM_STATE: type_GM_STATE

// known url for dummy image on Cin7 - don't have a better method than this
const knownCin7DefaultImageUrls = [
  'https://go.cin7.com/webfiles/SarahandSebastiaAU/webpages/images/621637/59111_PriceBreak_59150.jpg',
  'https://go.cin7.com/webfiles/SarahandSebastiaAU/webpages/images/621637/55710_PriceBreak_55749.jpg'
]

export const hasOwnProperties = (property: string, obj: object) => {
  return Object.prototype.hasOwnProperty.call(obj, property)
}

function normalizeStorefrontProduct(product: typeInStoreItemSupplementalRaw): typeInStoreItemSupplementalParsed | null {
  if (!product.handle || !product.variants) {
    console.error('Could not find handle or variants on product node while enriching wishlist items', product)
    return null
  }

  const variants = product.variants.nodes.map((variant) => {
    return {
      id: variant.id,
      availableForSale: variant.availableForSale,
      sku: variant.sku,
      price: variant.price.amount,
      selectedOptions: variant.selectedOptions
    }
  })

  return {
    id: product.id,
    title: product.title,
    handle: product.handle,
    tags: Array.isArray(product.tags) ? product.tags : [],
    productBiline: product.productBiline?.value || '',
    variants,
    skus: variants.map((variant) => variant.sku)
  }
}

function normalizeAjaxProduct(product: typeProduct): typeInStoreItemSupplementalParsed | null {
  if (!product?.handle || !Array.isArray(product.variants)) {
    console.error('Could not find handle or variants on ajax product while enriching wishlist items', product)
    return null
  }

  const variants = product.variants.map((variant) => ({
    id: String(variant.id),
    availableForSale: Boolean(variant.available),
    sku: variant.sku,
    price: String((variant.price ?? 0) / 100),
    selectedOptions: [variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .map((value, index) => ({
        name: product.options?.[index] || `Option ${index + 1}`,
        value
      }))
  }))

  return {
    id: String(product.id),
    title: product.title || '',
    handle: product.handle,
    tags: Array.isArray(product.tags) ? product.tags : [],
    productBiline: product.metafields?.productBiLine || '',
    variants,
    skus: variants.map((variant) => variant.sku)
  }
}

type WishlistComparableProperty = Record<string, any> & {
  properties: Record<string, any>
}

const WISHLIST_PROPERTY_ENTRY_KEYS = new Set([
  'core_product_multi_id',
  'core_product_single_id',
  'form_type',
  'id',
  'option-0',
  'option-1',
  'option-2',
  'option-3',
  'product-id',
  'properties',
  'quantity',
  'section-id',
  'utf8'
])

function isComparableRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sortObjectKeys<T extends Record<string, any>>(value: T): T {
  return Object.keys(value)
    .sort()
    .reduce(function (result: Record<string, any>, key) {
      result[key] = value[key]
      return result
    }, {}) as T
}

function parseComparableProperties(rawProperties: unknown) {
  if (typeof rawProperties === 'string') {
    try {
      return JSON.parse(rawProperties)
    } catch {
      return []
    }
  }

  return rawProperties
}

function normalizePropertyBag(propertyBag: unknown) {
  if (Array.isArray(propertyBag)) {
    return propertyBag.reduce((result: Record<string, any>, value) => {
      if (!isComparableRecord(value)) {
        return result
      }

      return {
        ...result,
        ...value
      }
    }, {})
  }

  if (isComparableRecord(propertyBag)) {
    return propertyBag
  }

  return {}
}

function toComparablePropertyList(rawProperties: unknown): Record<string, any>[] {
  const parsedProperties = parseComparableProperties(rawProperties)

  if (Array.isArray(parsedProperties)) {
    return parsedProperties.filter(isComparableRecord)
  }

  if (!isComparableRecord(parsedProperties)) {
    return []
  }

  const hasPropertyEntryKeys = Object.keys(parsedProperties).some((key) => WISHLIST_PROPERTY_ENTRY_KEYS.has(key))

  return [hasPropertyEntryKeys ? parsedProperties : { properties: parsedProperties }]
}

export function normalizeWishlistComparableProperties(rawProperties: unknown = []) {
  return toComparablePropertyList(rawProperties)
    .map((property: Record<string, any>) => {
      const normalizedProperty: WishlistComparableProperty = {
        ...property,
        properties: sortObjectKeys(normalizePropertyBag(property.properties))
      }

      if (normalizedProperty.hasOwnProperty('section-id')) {
        delete normalizedProperty['section-id']
      }
      if (normalizedProperty.properties.hasOwnProperty('_uniqueTimeStamp')) {
        normalizedProperty.properties._uniqueTimeStamp = 1
      }
      if (normalizedProperty.properties.hasOwnProperty('_uniqueItem')) {
        normalizedProperty.properties._uniqueItem = 1
      }
      if (normalizedProperty.properties.hasOwnProperty('pairId')) {
        normalizedProperty.properties.pairId = 1
      }

      delete normalizedProperty.form_type
      delete normalizedProperty.id
      delete normalizedProperty.quantity
      delete normalizedProperty.utf8
      delete normalizedProperty['product-id']
      delete normalizedProperty['option-1']
      delete normalizedProperty['option-2']

if (normalizedProperty.hasOwnProperty('core_product_single_id') && normalizedProperty.hasOwnProperty('core_product_multi_id')) {
         delete normalizedProperty['option-0']
       }
      return sortObjectKeys(normalizedProperty)
    })
    .filter((property: WishlistComparableProperty) => {
      const propertyKeys = Object.keys(property)

      if (!propertyKeys.length) {
        return false
      }

      if (propertyKeys.length === 1 && propertyKeys[0] === 'properties') {
        return Object.keys(property.properties || {}).length > 0
      }

      return true
    })
    .sort((propertyA: WishlistComparableProperty, propertyB: WishlistComparableProperty) => {
      return JSON.stringify(propertyA).localeCompare(JSON.stringify(propertyB))
    })
}

/**
 * Child items such as charms, letters, engravings should not be shown in the wishlist; nor items missing skus
 */
export const itemShouldBeHidden = (item: OmneoListItem | OmneoListItemInStore): boolean => {
  // to find the patterns of sku and title to use, run:
  /*
    let lists = await omneoService.sdk.get('lists').then(res => res.body.data)
    let allItems = lists.flatMap(list => list.items)
    let targetProps = allItems.map(item => { 
        const { title, sku } = item.product_variant
        return { title, sku }
    })
    console.table(targetProps)
  */

  const hideChildItemFragments = GM_STATE.wishlist.hideWishlistItemsBySkuFragment
  const { title, sku } = item.product_variant

  const titleContainsFragment = hideChildItemFragments.some((fragment) => {
    if (fragment === '') return false
    return title.includes(fragment)
  })
  const skuContainsFragment = hideChildItemFragments.some((fragment) => {
    if (fragment === '') return false
    return sku.includes(fragment)
  })
  const itemIsMissingSku = !item.product_variant.sku

  if (titleContainsFragment || skuContainsFragment || itemIsMissingSku) {
    return true
  }

  return false
}

/**
 * TODO - give this function a better name. It's scanning for item meta, not list validity
 */
export function listMadeOnSiteIsValid(list: OmneoList): boolean {
  // filter out any storemade list, with items, which has malformed meta props
  if (list.definition.type !== 'default') return true

  // allow empty lists
  if (!list.items.length) return true

  // only if all data is malformed - has bigger effect in detail page, causes a blank screen
  if (list.items.every((item) => item.meta === null)) return false

  return true
}

export function getWishlistListCoverImage(list: OmneoList): { src: string; alt: string } {
  const { imageDefault } = GM_STATE.wishlist
  let imageSrc =
    list.meta?.image?.src || list.items.find((x) => x?.meta?.image?.src)?.meta.image.src || list.items.find((x) => x?.meta?.image)?.meta.image || ''

  if (!imageSrc) {
    const itemWithImage = list.items.find((item) => {
      if (itemShouldBeHidden(item)) return false

      const firstImageUrl = item.product?.images?.[0]?.url
      // known url for dummy image on Cin7 - don't have a better method than this
      if (knownCin7DefaultImageUrls.includes(firstImageUrl)) return false

      return item.product?.images?.[0]?.url
    })
    if (itemWithImage) {
      imageSrc = itemWithImage.product.images[0].url
    }
  }

  const image = {
    src: imageSrc?.toString() || imageDefault.src,
    alt: list.name || imageDefault.alt || ''
  }

  return image
}

async function executeStorefrontWishlistSearch(gqlQuery: { query: string; variables: { query: string; first: number } }) {
  return await executeGraphQlQuery(gqlQuery)
    .then((res) => {
      if (!res.ok) {
        console.error('res not ok', res)
        throw res
      }
      return res.json()
    })
    .then((data) => {
      if (data.errors) {
        throw data
      }

      return data.data.search.nodes
        .map((product: typeInStoreItemSupplementalRaw) => normalizeStorefrontProduct(product))
        .filter((product: typeInStoreItemSupplementalParsed | null): product is typeInStoreItemSupplementalParsed => Boolean(product))
    })
}

export async function getStorefrontProductsBySkus(skus: string[]): Promise<Array<typeInStoreItemSupplementalParsed>> {
  const gqlQuery = getManyProductsByManySkus(skus)
  return await executeStorefrontWishlistSearch(gqlQuery)
    .catch((err) => {
      console.error('Could not fetch products from storefront during getLists', err)
      return []
    })
}

export async function getStorefrontProductsByHandles(handles: string[]): Promise<Array<typeInStoreItemSupplementalParsed>> {
  const gqlQuery = getManyProductsByManyHandles(handles)
  return await executeStorefrontWishlistSearch(gqlQuery)
    .catch((err) => {
      console.error('Could not fetch products by handle from storefront during getLists', err)
      return []
    })
}

export async function getAjaxProductsByHandles(handles: string[]): Promise<Array<typeInStoreItemSupplementalParsed>> {
  const uniqueHandles = [...new Set(handles.filter(Boolean))]

  const products = await Promise.all(
    uniqueHandles.map(async (handle) => {
      try {
        const product = await fetchData(`/products/${handle}?view=ajax`) as typeProduct | undefined
        return product ? normalizeAjaxProduct(product) : null
      } catch (error) {
        console.error('Could not fetch product from ajax while enriching wishlist items', handle, error)
        return null
      }
    })
  )

  return products.filter((product): product is typeInStoreItemSupplementalParsed => Boolean(product))
}

export function attachRequiredStorefrontInformationToOmneoItem(
  item: OmneoListItem | OmneoListItemInStore,
  storefrontProducts: typeInStoreItemSupplementalParsed[],
  listDefinition: OmneoListDefinitionType
): OmneoListItem | OmneoListItemInStore {
  const sku = item.product_variant.sku
  const storefrontProduct = storefrontProducts.find((product) => product.skus.includes(sku))

  item.meta = item.meta || {}

  if (!storefrontProduct) {
    if (item.product?.status && item.product.status !== 'active') {
      item.meta.hidden = true
      return item
    }

    item.meta.discontinued = true
    item.meta.variantAvailable = false
    return item
  }

  const storefrontVariantPrice = storefrontProduct?.variants.find((variant) => variant.sku === sku)?.price ?? ''
  const storefrontVariant = storefrontProduct?.variants.find((variant) => variant.sku === sku)
  const variantId = getIdFromGqlAsNumber(storefrontVariant?.id || '')
  const productId = getIdFromGqlAsNumber(storefrontProduct?.id || '')

  delete item.meta.discontinued
  delete item.meta.hidden

  item.product.tags = storefrontProduct?.tags || []

  // CDSD-29896
  // always need to forcefully set the price - the Omneo list currently stores the price, but doesn't know if its correct or not
  if (storefrontVariantPrice) {
    item.meta.variantPrice = storefrontVariantPrice
  }

  item.meta.variantAvailable = storefrontVariant?.availableForSale || false

  // don't need to redefine any data on any list EXCEPT those made in Omneo
  if (listDefinition !== 'store') return item

  const handle = storefrontProduct?.handle || ''
  const title = storefrontProduct?.title || item.product.title

  const productBiline = storefrontProduct?.productBiline || ''
  // TODO: get variant price with particular sku

  item.product.handle = handle
  item.product.title = title

  item.meta.productBiline = productBiline
  item.meta.handle = handle
  if (typeof item.meta.templateSuffix !== 'string' || !item.meta.templateSuffix.trim()) {
    item.meta.templateSuffix = 'default'
  }
  item.meta.variantOptions = storefrontVariant?.selectedOptions.map((option) => option.value) || []
  item.meta.variantSku = sku
  item.meta.variantId = variantId
  item.meta.productId = productId

  return item
}
